import { useEffect, useMemo } from "react"
import { useParams } from "react-router-dom"

import { useLazyQuery } from "@apollo/client"

import styles from "./RereservationCompletePage.module.scss"

import CompleteIcon from "@/public/components/ui/CompleteIcon/CompleteIcon"
import { graphql } from "@/public/gql/__generated__/gql"
import { formatJstDateUntilDay, formatJstHourMinuteOfDate } from "@/utils/date"
import { toStringWithCommas } from "@/utils/strings/number"

const Rereservation = graphql(/* GraphQL */ `
	query GetRereservation($param: RereservationParam!) {
		Rereservation(param: $param) {
			id
			customer {
				name
			}
			staff {
				name
			}
			menusAndCoupons {
				names
			}
			startTime
			price
			durationMinute
			salon {
				name
				durationMinuteForEmptyMenu
				cancelConditionTextsForMenu
				cancelConditionTextsForNoMenu
			}
			useClaimingConversionRecancelPolicy
		}
	}
`)

const RereservationCompletePage = () => {
	const cancelID = useParams().cancel_id

	const [getRereservation, getRereservationResult] = useLazyQuery(Rereservation)
	const rereservation = getRereservationResult.data?.Rereservation

	const menusAndCoupons = rereservation?.menusAndCoupons
	const menusAndCouponsLength = menusAndCoupons?.names.length ?? 0
	const cancelConditionTexts =
		menusAndCouponsLength > 0
			? rereservation?.salon.cancelConditionTextsForMenu
			: rereservation?.salon.cancelConditionTextsForNoMenu

	useEffect(() => {
		if (!cancelID) {
			return
		}
		getRereservation({ variables: { param: { cancelId: cancelID } } })
	}, [cancelID, getRereservation])

	const durationMinute = useMemo(() => {
		return rereservation?.durationMinute + "分"
	}, [rereservation?.durationMinute])
	const menuNames = useMemo(() => {
		const names = rereservation?.menusAndCoupons?.names
		if (!names || names.length === 0) {
			return ["なし"]
		}

		const texts: string[] = []
		for (const name of names) {
			texts.push(`・${name}`)
		}
		return texts
	}, [rereservation?.menusAndCoupons?.names])
	const price = useMemo(() => {
		return "¥" + toStringWithCommas(rereservation?.price ?? 0) // FIXME:
	}, [rereservation?.price])
	const staffName = useMemo(() => {
		return rereservation?.staff?.name ?? "なし"
	}, [rereservation?.staff?.name])

	if (getRereservationResult.loading) {
		return <div>読み込み中...</div>
	}

	return (
		<div className={styles.complete_page}>
			<div className={styles.icon_container}>
				<CompleteIcon />
			</div>
			<h2>再予約が確定しました</h2>
			<div className={styles.box}>
				<div className={styles.box_title}>■ご予約内容</div>
				<div className={styles.main_content_container}>
					<table>
						<tr>
							<td className={styles.title}>ご予約店舗</td>
							<td className={styles.content}>{rereservation?.salon.name}</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約日時</td>
							<td className={styles.content}>
								{rereservation?.startTime
									? formatJstDateUntilDay(new Date(rereservation.startTime))
									: ""}
							</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約時間</td>
							<td className={styles.content}>
								{rereservation?.startTime
									? formatJstHourMinuteOfDate(new Date(rereservation.startTime))
									: ""}
							</td>
						</tr>
						<tr>
							<td className={styles.title}>施術時間</td>
							<td className={styles.content}>{durationMinute}</td>
						</tr>

						<br />

						<tr>
							<td className={styles.title}>ご予約メニュー</td>
							<td className={styles.content}>
								{menuNames.map((name, i) => (
									<div key={i}>{name}</div>
								))}
							</td>
						</tr>
						<tr>
							<td className={styles.title}>ご予約金額</td>
							<td className={styles.content}>{price}</td>
						</tr>
						<tr>
							<td className={styles.title}>指名スタッフ</td>
							<td className={styles.content}>{staffName}</td>
						</tr>

						<br />

						<tr>
							<td className={styles.title}>お客様名</td>
							<td className={styles.content}>{rereservation?.customer.name}</td>
						</tr>
					</table>
				</div>
			</div>

			<div className={styles.note_wrapper}>
				<p className={styles.note_text}>※ 別途指名料がかかる場合がございます</p>
				<p className={styles.note_text}>
					※ 本フォームからの予約ではポイントは付与されません
				</p>
			</div>

			{cancelConditionTexts && cancelConditionTexts.length > 0 && (
				<div className={styles.box}>
					<div className={styles.box_title}>■キャンセルポリシー</div>
					<div className={styles.cancel_policy_container}>
						{rereservation?.useClaimingConversionRecancelPolicy ? (
							<>
								<div>
									直前のご予約変更・キャンセル、無断キャンセル、遅刻などが発生した場合、他のお客様へのご迷惑となるだけでなく、時間的・金銭的な損失が生じてしまいます。
								</div>
								<br />
								<div>
									そのため、やむを得ず下記のキャンセル料を設定しております。
									<br />{" "}
									なお、今回のご予約につきましては、すでに一度キャンセル料を免除させていただいておりますため、いかなる理由であってもキャンセル料が発生いたします。
								</div>
								<br />
								<div>あらかじめご了承ください。</div>
							</>
						) : (
							<>
								<div>
									直前のご予約変更やキャンセル、無断キャンセル、遅刻などがありますと、
								</div>
								<div>
									他のお客様にもご迷惑がかかるだけでなく、時間をロスしてしまい、
								</div>
								<div>その分、余計なコストが発生してしまいます。 </div>

								<br />

								<div>
									そうすると、その分の料金を上げさせていただかなければいけなくなります。{" "}
								</div>
								<div>
									少しでも、そのような事態を避けるための取り組みの一環として、
								</div>
								<div>
									{" "}
									キャンセル料を以下のように設定させて頂いております。{" "}
								</div>

								<br />
							</>
						)}

						<div>
							<b>＜キャンセル料＞</b>
						</div>
						{cancelConditionTexts?.map((text, i) => {
							return (
								<div>
									{" "}
									<b key={i}>{text}</b>{" "}
								</div>
							)
						})}

						<br />
						<div>ご理解とご協力を何卒宜しくお願い致します。</div>
					</div>
				</div>
			)}

			<div className={styles.notice}>
				※ キャンセルをご希望の方は店舗に直接お電話ください。
			</div>
		</div>
	)
}

export default RereservationCompletePage
